import {Button, ContentTemplate, Grid} from 'web-library';
import React, {useState} from 'react';

import {RouteComponentProps, useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {MutationStatus, UseMutateFunction} from 'react-query';
import {AxiosError} from 'axios';
import PaymentButton, {PaymentMethod} from '../../../modules/payment/components/PaymentButton';
import {ApplicationType} from '../../../api/candidate/config/model';
import BankTransferModal from '../../../modules/payment/components/BankTransferModal';
import {
  ConfirmAndPayRequest,
  ConfirmAndPayResponse,
  usePostCoordinatorAppealConfirmAndPay,
} from '../../../api-refactored/appeal/usePostCoordinatorAppealConfirmAndPay';
import {selectBankTransferOption} from '../../../store/resitSlice';
import {GenericErrorResponse} from '../../../api';
import {fallbackEmptyString} from '../../../utils/StringUtils';
import {
  ExamPaymentModeKey
} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import RouteKey from '../../../routes/key';
import {useCustomMutate} from '../../../hooks/query';
import {postPaymentOffline} from '../../../api/financial/api';
import {useGetPaymentAccess} from '../../../api-refactored/financial/useGetPaymentAccess';

const {receipt, applicationsCoordinator} = RouteKey.appeal.child;
const {appeal} = RouteKey.child;

const handlePaymentOption = (
    callback: (internalTransactionRef: string) => void,
    mutatePostConfirmAndPay: UseMutateFunction<
        ConfirmAndPayResponse,
        AxiosError<GenericErrorResponse>,
        ConfirmAndPayRequest
    >,
    uuid: string,
    method?: string
) => {
  mutatePostConfirmAndPay(
      {
        uuid,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res: ConfirmAndPayResponse) => {
          callback(res.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
  );
};

const handleClickPaymentButton = (
    callback: () => void,
    isDeclared: boolean,
    mutatePostConfirmAndPay: UseMutateFunction<
        ConfirmAndPayResponse,
        AxiosError<GenericErrorResponse>,
        ConfirmAndPayRequest
    >,
    {uuid, history}: { uuid: string; history: RouteComponentProps['history'] },
    {
      setErrMsg,
      setPaymentModeNotSelectedErrMsg,
      setIsBankTransferModalOpen,
    }: {
      setErrMsg: React.Dispatch<React.SetStateAction<boolean>>;
      setPaymentModeNotSelectedErrMsg: React.Dispatch<React.SetStateAction<boolean>>;
      setIsBankTransferModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    },
    selectedPaymentMode: ExamPaymentModeKey
) => {
  const handleNotDeclaredOrProceedFlow = () => {
    if (!isDeclared) {
      setErrMsg(true);
    } else {
      setErrMsg(false);
      if (selectedPaymentMode === ExamPaymentModeKey.coordinatorPayOption) {
        callback();
      }
      if (selectedPaymentMode === ExamPaymentModeKey.bankTransferOption) {
        setIsBankTransferModalOpen(true);
      }
      if (selectedPaymentMode === ExamPaymentModeKey.candidatePayOption) {
        mutatePostConfirmAndPay(
            {
              uuid,
              paymentMode: 'PAYMENT_LINK',
            },
            {
              onSuccess: (res: ConfirmAndPayResponse) => {
                handleSuccessfulRouting(selectedPaymentMode, uuid, res, history);
              },
            }
        );
      }
    }
    setPaymentModeNotSelectedErrMsg(false);
  };
  if (selectedPaymentMode === ExamPaymentModeKey.noPaymentModeSelected) {
    setPaymentModeNotSelectedErrMsg(true);
    setErrMsg(!isDeclared);
  } else {
    handleNotDeclaredOrProceedFlow();
  }
};

const handleSuccessfulRouting = (
    selectedPaymentMode: ExamPaymentModeKey,
    uuid: string,
    res: ConfirmAndPayResponse,
    history: RouteComponentProps['history']
) => {
  if (
      selectedPaymentMode ===
      (ExamPaymentModeKey.bankTransferOption || ExamPaymentModeKey.coordinatorPayOption)
  ) {
    history.push(
        `/${appeal}/${applicationsCoordinator.route}/${uuid}/${receipt.route}/${res.internalTransactionRef}`
    );
  }
  if (selectedPaymentMode === ExamPaymentModeKey.candidatePayOption) {
    history.push(`/${appeal}/${applicationsCoordinator.route}/${uuid}/${receipt.route}`);
  }
};

const paymentButtonIsLoadingOption = (
    selectedPaymentMode: ExamPaymentModeKey,
    isLoadingConfirmAndPay: boolean
) =>
    selectedPaymentMode === ExamPaymentModeKey.bankTransferOption ||
    selectedPaymentMode === ExamPaymentModeKey.candidatePayOption
        ? isLoadingConfirmAndPay
        : false;

const onBack = (history: RouteComponentProps['history']) => {
  history.goBack();
};

const statusPaymentOffline = (statusConfirmAndPay: MutationStatus) =>
    statusConfirmAndPay !== 'success' ? statusConfirmAndPay : 'idle';

interface CoordinatorReviewAppealButtonsProps {
  classes: Record<string,string>;
  uuid: string;
  isPaymentDisabled: boolean;
  setIsPaymentDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPaymentMode: ExamPaymentModeKey;
  isDeclared: boolean;
  setErrMsg: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentModeNotSelectedErrMsg: React.Dispatch<React.SetStateAction<boolean>>;
}

const CoordinatorReviewAppealButtons = ({
                                          classes,
                                          uuid,
                                          isPaymentDisabled,
                                          setIsPaymentDisabled,
                                          selectedPaymentMode,
                                          isDeclared,
                                          setErrMsg,
                                          setPaymentModeNotSelectedErrMsg,
                                        }: CoordinatorReviewAppealButtonsProps) => {
  const history = useHistory();
  const {t} = useTranslation();

  const bankTransferEnabled = useSelector(selectBankTransferOption);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
      Array<PaymentMethod>
  >([]);

  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState<boolean>(false);

  const {mutate: mutatePostPaymentOffline} = useCustomMutate(
      'POST_MANUAL_BANK_TRANSFER',
      postPaymentOffline
  );

  const {
    mutate: mutatePostConfirmAndPay,
    isLoading: isLoadingConfirmAndPay,
    status: statusConfirmAndPay,
  } = usePostCoordinatorAppealConfirmAndPay({
    onSuccess: (res: ConfirmAndPayResponse) => {
      if (selectedPaymentMode === ExamPaymentModeKey.bankTransferOption) {
        mutatePostPaymentOffline(res.internalTransactionRef);
      }
      if (res.paymentSuccess) {
        history.push(
            `/${appeal}/${applicationsCoordinator.route}/${uuid}/${receipt.route}?internalTransactionRef=${res.internalTransactionRef}`
        );
      }
    },
  });

  useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod || bankTransferEnabled) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  return (
      <ContentTemplate continuous>
        <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={3}
            className={classes.buttonContainer}
        >
          <Grid xs={12} md={3}>
            <Button
                id="button-selectSubject-back"
                fullWidth
                className={classes.button}
                variant="outlined"
                onClick={() => {
                  onBack(history);
                }}
            >
              {t('appeal:ReviewAppeal.Back')}
            </Button>
          </Grid>
          <Grid xs={12} md={4}>
            <PaymentButton
                buttonClassName={classes.paymentButton}
                isUniqueLink={false}
                disabled={isPaymentDisabled}
                onPaymentComplete={(isRetry) => {
                  setIsPaymentDisabled(isRetry);
                }}
                paymentMethodsAvailable={paymentAccessDetailsOption}
                onClickPaymentButton={(callback) => {
                  handleClickPaymentButton(
                      callback,
                      isDeclared,
                      mutatePostConfirmAndPay,
                      {uuid, history},
                      {
                        setErrMsg,
                        setPaymentModeNotSelectedErrMsg,
                        setIsBankTransferModalOpen,
                      },
                      selectedPaymentMode
                  );
                }}
                onHandlePaymentMethod={(callback, method) => {
                  handlePaymentOption(callback, mutatePostConfirmAndPay, uuid, method);
                }}
                onSuccessPayment={(txnId) =>
                    history.push(
                        `/${appeal}/${applicationsCoordinator.route}/${uuid}/${receipt.route}?internalTransactionRef=${txnId}`
                    )
                }
                fullWidth
                buttonText={t('appeal->>AppealCoordinatorReviewApplication.ProceedWithAppeal')}
                buttonErrorText={t('appeal->>AppealReview.BackToAppealApplications')}
                buttonVariant="contained"
                type={ApplicationType.APPEAL}
                isLoading={paymentButtonIsLoadingOption(selectedPaymentMode, isLoadingConfirmAndPay)}
            />
            <BankTransferModal
                modelOpen={isBankTransferModalOpen}
                onClose={() => {
                  setIsBankTransferModalOpen(false);
                }}
                onClickCheckout={() => {
                  setIsBankTransferModalOpen(false);
                  mutatePostConfirmAndPay(
                      {
                        uuid,
                        paymentMode: 'BANK_TRANSFER',
                      },
                      {
                        onSuccess: (res: ConfirmAndPayResponse) => {
                          handleSuccessfulRouting(selectedPaymentMode, uuid, res, history);
                        },
                      }
                  );
                }}
                statusPaymentOffline={statusPaymentOffline(statusConfirmAndPay)}
            />
          </Grid>
        </Grid>
      </ContentTemplate>
  );
};

export default CoordinatorReviewAppealButtons;
